import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getPermissions } from '../utils/permissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('lenmo_token');
    const storedUser = localStorage.getItem('lenmo_user');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = useCallback((tokenData, userData) => {
    localStorage.setItem('lenmo_token', tokenData);
    localStorage.setItem('lenmo_user', JSON.stringify(userData));
    setToken(tokenData);
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('lenmo_token');
    localStorage.removeItem('lenmo_user');
    setToken(null);
    setUser(null);
  }, []);

  const permissions = useMemo(
    () => getPermissions(user?.type === 'staff' ? user?.role : null),
    [user]
  );

  const isStaff = user?.type === 'staff';
  const isMember = user?.type === 'member';
  const isAdmin = isStaff && user?.role === 'admin';
  const isSupervisor = isStaff && user?.role === 'supervisor';
  const isMarketing = isStaff && user?.role === 'marketing';
  const isAccountant = isStaff && user?.role === 'accountant';
  const isViewer = isStaff && user?.role === 'viewer';
  const isSystemAdmin = isAdmin;
  /** Can create/update on at least one section (not read-only viewer) */
  const canMutate = isStaff && permissions.canMutate;

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout,
      permissions,
      isAdmin, isSupervisor, isMarketing, isAccountant, isViewer,
      isStaff, isMember, isSystemAdmin, canMutate,
      canAccess: permissions.canAccess,
      canWrite: permissions.canWrite,
      canDelete: permissions.canDelete,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
